import React from 'react';

const CONTINUE = { yes: 'Yes', maybe: 'Maybe', no: 'No' };

function Stat({ label, value, testId }) {
  return (
    <div className="bg-slate-900 rounded-lg p-3" data-testid={testId}>
      <p className="text-2xl font-bold text-white">{value ?? '—'}</p>
      <p className="text-slate-400 text-xs">{label}</p>
    </div>
  );
}

function Answer({ q, a }) {
  if (!a) return null;
  return <p className="text-sm"><span className="text-slate-400">{q}: </span><span className="text-slate-200">{a}</span></p>;
}

export function PilotResultsCard({ results }) {
  if (!results) return null;
  const { summary: s, pro_surveys, session_notes, family_surveys } = results;
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 space-y-5" data-testid="pilot-results-card">
      <h3 className="text-white font-semibold">Pilot results — what you're learning</h3>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Stat label="Pro ease of use (1–5)" value={s.avg_pro_ease} testId="stat-pro-ease" />
        <Stat label="Pros who'd continue" value={`${s.pros_would_continue}/${pro_surveys.length}`} testId="stat-pro-continue" />
        <Stat label="Families joined" value={results.families_joined} testId="stat-families" />
        <Stat label="Family usefulness (1–5)" value={s.avg_family_usefulness} testId="stat-family-useful" />
        <Stat label="Family trust (1–5)" value={s.avg_family_trust} testId="stat-family-trust" />
        <Stat label="Follow-ups wanted" value={s.family_follow_ups} testId="stat-follow-ups" />
      </div>

      <section>
        <h4 className="text-emerald-300 text-sm font-semibold mb-2">Professional feedback</h4>
        {pro_surveys.length === 0 && <p className="text-slate-500 text-sm">None yet.</p>}
        {pro_surveys.map(p => (
          <div key={p.profile_id} className="bg-slate-900 rounded-lg p-3 mb-2 space-y-1" data-testid={`pro-survey-${p.profile_id}`}>
            <p className="text-white font-medium">{p.name} · ease {p.ease_rating}/5 · would continue: {CONTINUE[p.would_continue]}</p>
            <Answer q="Values most" a={p.value_most} />
            <Answer q="Missing" a={p.missing} />
            <Answer q="How families benefit" a={p.family_benefit} />
            <Answer q="Fair price" a={p.fair_price} />
            <Answer q="Other" a={p.comments} />
          </div>
        ))}
      </section>

      <section>
        <h4 className="text-emerald-300 text-sm font-semibold mb-2">Professionals' session notes (private to you)</h4>
        {session_notes.length === 0 && <p className="text-slate-500 text-sm">None yet.</p>}
        {session_notes.map(n => (
          <div key={n.note_id} className="bg-slate-900 rounded-lg p-3 mb-2" data-testid={`session-note-${n.note_id}`}>
            <p className="text-white text-sm font-medium">{n.session_label} <span className="text-slate-400 font-normal">· {n.professional_name}{n.session_date ? ` · ${n.session_date}` : ''}{n.attendees != null ? ` · ${n.attendees} attended` : ''}</span></p>
            <p className="text-slate-300 text-sm whitespace-pre-wrap">{n.notes}</p>
          </div>
        ))}
      </section>

      <section>
        <h4 className="text-emerald-300 text-sm font-semibold mb-2">Family feedback after presentations</h4>
        {family_surveys.length === 0 && <p className="text-slate-500 text-sm">None yet — opens with Phase 2.</p>}
        {family_surveys.map(f => (
          <div key={`${f.presentation_id}-${f.user_id}`} className="bg-slate-900 rounded-lg p-3 mb-2 space-y-1">
            <p className="text-white text-sm font-medium">{f.name} on "{f.presentation_title}" · useful {f.usefulness}/5 · trust {f.trust}/5 {f.want_follow_up && <span className="text-amber-300">· wants follow-up ({f.email})</span>}</p>
            <Answer q="Liked" a={f.liked} />
            <Answer q="Improve" a={f.improve} />
          </div>
        ))}
      </section>
    </div>
  );
}
